import {
  OrcaDeployActivityLogRow,
  OrcaDeployState,
  OrcaDeployStateValue,
  OrcaProdUrlEntry,
  OrcaServerSlot,
  ServerSlotKey,
} from "./orcaDeployActivityLogsTypes";

// SIM-954 state machine, confirmed with product:
//   Deploy:                stage(0)     -> stable(1),   prod/second SWAP
//   Rollback:               stable(1)    -> stage(0),    prod/second SWAP
//   Display Old Version Link: stable(1) -> unstable(2),  pointers unchanged
//   Hide Old Version Link:  unstable(2) -> stable(1),    pointers unchanged
//   Stage:                  stable(1)    -> stage(0),    pointers unchanged
// Deploy and Rollback are mirror images of the same "promote second to prod"
// operation, just triggered in opposite directions — they share swapPointers.
// `result` is always derived from prod/second + state, never edited directly.

const getSlot = (
  row: Pick<OrcaDeployActivityLogRow, "server1" | "server2" | "server3" | "server4">,
  key: ServerSlotKey
): OrcaServerSlot | null => row[key];

// SIM-954 slot url semantics: admin only maintains the bare domain (no
// protocol, no path), and this assembles the full SSO URL ares.web opens.
const ORCA_SSO_PATH = "/web/sso?token=";

const toEntry = (
  slot: OrcaServerSlot,
  name: string,
  description: string
): OrcaProdUrlEntry => ({
  server: `https://${slot.url}${ORCA_SSO_PATH}`,
  name,
  description,
  hash: slot.hash,
  version: slot.version,
});

// Builds ares.web's `result` array — the shape ares.web actually renders as
// buttons, not the raw server1-4 slot shape. result[0] is always prod. Only
// non-stable states include a second entry, and which link fields it uses
// depends on WHY it's non-stable: stage means second is a Beta build
// under test (betaLinkName/Description); unstable means second is the old
// build kept around as a fallback while prod is suspect
// (fallbackLinkName/fallbackDescription). These are different audiences —
// Beta is gated to opted-in users upstream, fallback must reach everyone —
// so picking the wrong one silently mislabels/misscopes the button.
export const deriveResult = (
  row: Pick<
    OrcaDeployActivityLogRow,
    | "server1"
    | "server2"
    | "server3"
    | "server4"
    | "prod"
    | "second"
    | "state"
    | "prodLinkName"
    | "prodLinkDescription"
    | "fallbackLinkName"
    | "fallbackDescription"
    | "betaLinkName"
    | "betaLinkDescription"
  >
): OrcaProdUrlEntry[] => {
  const prodSlot = getSlot(row, row.prod);
  if (!prodSlot) return [];
  const prodEntry = toEntry(prodSlot, row.prodLinkName, row.prodLinkDescription);
  if (row.state === OrcaDeployState.STABLE) return [prodEntry];

  const secondSlot = getSlot(row, row.second);
  if (!secondSlot) return [prodEntry];

  const secondEntry =
    row.state === OrcaDeployState.UNSTABLE
      ? toEntry(secondSlot, row.fallbackLinkName, row.fallbackDescription)
      : toEntry(secondSlot, row.betaLinkName, row.betaLinkDescription);
  return [prodEntry, secondEntry];
};

type BaseFields = Pick<
  OrcaDeployActivityLogRow,
  | "server1"
  | "server2"
  | "server3"
  | "server4"
  | "prod"
  | "second"
  | "state"
  | "prodLinkName"
  | "prodLinkDescription"
  | "fallbackLinkName"
  | "fallbackDescription"
  | "betaLinkName"
  | "betaLinkDescription"
>;

const withDerivedResult = <T extends BaseFields>(
  next: T
): T & { result: OrcaProdUrlEntry[] } => ({
  ...next,
  result: deriveResult(next),
});

export const swapPointers = (latest: BaseFields, toState: OrcaDeployStateValue) =>
  withDerivedResult({
    ...latest,
    prod: latest.second,
    second: latest.prod,
    state: toState,
  });

export const deploy = (latest: BaseFields) =>
  swapPointers(latest, OrcaDeployState.STABLE);

export const rollback = (latest: BaseFields) =>
  swapPointers(latest, OrcaDeployState.STAGE);

export const displayOldVersionLink = (latest: BaseFields) =>
  withDerivedResult({ ...latest, state: OrcaDeployState.UNSTABLE });

export const hideOldVersionLink = (latest: BaseFields) =>
  withDerivedResult({ ...latest, state: OrcaDeployState.STABLE });

export const stage = (latest: BaseFields) =>
  withDerivedResult({ ...latest, state: OrcaDeployState.STAGE });

type SlotFields = Pick<BaseFields, "server1" | "server2" | "server3" | "server4">;

type LinkNameFields = Pick<
  BaseFields,
  | "prodLinkName"
  | "prodLinkDescription"
  | "fallbackLinkName"
  | "fallbackDescription"
  | "betaLinkName"
  | "betaLinkDescription"
>;

type PointerFields = Pick<BaseFields, "prod" | "second">;

// The single edit path for any number of slots' url/hash/version at once
// (e.g. server1 and server2 together) plus the three link names/
// descriptions, and optionally which slots prod/second point at (this is
// how second gets pointed at server3/server4 — there was never a schema
// restriction to server1/server2, just no UI for it). Replaces each given
// slot wholesale (not a hash-only patch) — this is also the only way to put
// a url on server3/4, which Create New never touches. Callers should pass
// every slot/link field (using the caller's own edited value for ones the
// user touched, and the freshly re-fetched latest row's value for ones they
// didn't) so untouched fields reflect the true latest state rather than
// whatever was loaded when the edit dialog was opened. `pointers` is
// optional because prod/second are normally locked to the pre-existing
// values (see the stage-only gating in the Edit dialog).
export const editSlotsAndLinks = (
  latest: BaseFields,
  slots: SlotFields,
  linkNames: LinkNameFields,
  pointers?: PointerFields
) =>
  withDerivedResult({
    ...latest,
    ...slots,
    ...linkNames,
    ...(pointers ?? {}),
  });
